import React from 'react';
import { Card, theme } from 'antd';

const { useToken } = theme;

function formatAmount(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

interface MetricCardProps {
  title: string;
  count: number;
  amount?: number;
  hint?: string;
  accent?: 'default' | 'success' | 'error' | 'warning';
  icon?: React.ReactNode;
  onClick?: () => void;
}

const BusinessOverviewMetricCard: React.FC<MetricCardProps> = ({
  title,
  count,
  amount,
  hint,
  accent = 'default',
  icon,
  onClick,
}) => {
  const { token } = useToken();
  const accentColor =
    accent === 'default'
      ? token.colorPrimary
      : accent === 'success'
        ? token.colorSuccess
        : accent === 'error'
          ? token.colorError
          : token.colorWarning;

  return (
    <Card
      bordered={false}
      style={{
        height: '100%',
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        position: 'relative',
        overflow: 'hidden',
      }}
      styles={{
        body: {
          padding: '20px 20px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        },
      }}
      className={onClick ? 'metric-card-clickable' : undefined}
      onClick={onClick}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 4,
          height: '100%',
          background: accentColor,
          borderRadius: '0 2px 2px 0',
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: token.colorTextSecondary,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </span>
        {icon && (
          <span style={{ color: accentColor, opacity: 0.7, display: 'flex', fontSize: 16 }}>
            {icon}
          </span>
        )}
      </div>
      <span
        style={{
          fontSize: 32,
          fontWeight: 700,
          lineHeight: 1.1,
          color: token.colorText,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {count.toLocaleString()}
      </span>
      {amount !== undefined && (
        <span
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: token.colorTextSecondary,
            marginTop: 4,
          }}
        >
          {formatAmount(amount)} 
          <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.6 }}>NPR</span>
        </span>
      )}
      {hint && (
        <span
          style={{
            fontSize: 11,
            color: token.colorTextSecondary,
            opacity: 0.75,
            marginTop: 4,
            lineHeight: 1.3,
          }}
        >
          {hint}
        </span>
      )}
    </Card>
  );
};

export { formatAmount };
export default BusinessOverviewMetricCard;
